import { readOnlyAnnouncementID } from "#skyward_global";
import { ComponentInstance, computed, defineCustomElement, nextTick, onBeforeMount, onMounted, onUnmounted, ref, useHost, watch } from "vue";
import { nextAutoId } from "../../skyward.base.extensions";
import { DAYS_OF_WEEK, formatServerDate, now, parseDate } from "../../skyward.base.time";
import VueModalLayer from "../core/modalLayer";
import Svg from "../core/svg";
import { OptionalVueProp } from "../core/vueHelpers";
import { CalendarExposed } from "../datePicker/components/Calendar";
import { TimeSelectorExposed } from "../timePicker/components/TimeSelector";
import DateTimePickerModal from "./DateTimePickerModal";

const DateTimePickerClasses =
{
    input: "dateTimePicker",
    svg: "dateTimePicker__svg",
    helpText: "datePicker__helpText"
};

enum DateTimeParts
{
    Month = "M",
    Day = "D",
    Year = "Y",
    Hour = "H",
    Minute = "MM",
    AmPm = "X"
}

const DAY_OF_WEEK_END_POSITION = 14;
const HOUR_START_POSITION = 12;
const MINUTE_START_POSITION = 15;
const END_POSITION = 19;

const DateTimePicker = /*#__PURE__*/ defineCustomElement({
    name: "sky-datetimepicker",
    shadowRoot: false,
    inheritAttrs: false,
    props: {
        name: OptionalVueProp(String),
        value: {
            type: String,
            required: false,
            default: null,
            validator(value: any)
            {
                return !value || typeof value === "string" && parseDate(value).isValidDate();
            }
        },
        autocomplete: OptionalVueProp(String),
        dependents: OptionalVueProp(Object),
        ariaLabelledby: OptionalVueProp(String),
        contained: OptionalVueProp(Boolean)
    },
    setup(props, context)
    {
        const inputRef = ref<HTMLInputElement>();
        const suppressNextFocus = ref<Boolean>(false);
        let rootElem = useHost() as any as (HTMLElement & { value: string | undefined }) | null;
        const resolveRootElem = () =>
        {
            if (!rootElem)
            {
                rootElem = inputRef.value?.closest("sky-datetimepicker") as (HTMLElement & { value: string | undefined }) | null;
            }
            return rootElem;
        };
        const getAriaReadonly = () => resolveRootElem()?.getAttribute("aria-readonly")?.toLowerCase() === "true";
        const readonly = ref(getAriaReadonly());
        // Read from the host element rather than declaring an "id" prop, which would shadow the native HTMLElement.id property
        const getHostId = () => resolveRootElem()?.id || undefined;
        const hostId = ref(getHostId());

        const value = computed<string | undefined>({
            get: () => props.value,
            set: (newValue) =>
            {
                const currentRoot = resolveRootElem();
                if (currentRoot)
                {
                    currentRoot.value = newValue;
                }
            }
        });

        watch(() => props.value, (newVal) =>
        {
            if (!(!newVal || typeof newVal === "string" && parseDate(newVal).isValidDate()))
            {
                value.value = "";
            }
        },
        {
            flush: "sync",
            immediate: true
        });

        const valueAsDate = computed<Date | undefined>(
        {
            get: () =>
            {
                if (props.value === "" || props.value === null || props.value === undefined)
                {
                    return undefined;
                }
                return parseDate(props.value);
            },
            set: (newValue) =>
            {
                value.value = newValue?.isValidDate() ? formatServerDate(newValue) : "";
            }
            });

        // needs to be exposed as a function, otherwise Vue will deref it
        context.expose(
            {
                _valueAsDateRef: () => valueAsDate,
                _closeModal: closeModal
            });

        const modalOpen = ref<true | false | "closing">(false);

        onBeforeMount(() =>
        {
            if (hostId.value)
            {
                $.findLabel(hostId.value).attr("for", `${hostId.value}_input`);
            }
        });

        const observer = new MutationObserver(() =>
        {
            readonly.value = getAriaReadonly();
            hostId.value = getHostId();
        });

        onMounted(() =>
        {
            if (props.ariaLabelledby)
            {
                inputRef.value?.setAttribute("aria-labelledby", props.ariaLabelledby);
                resolveRootElem()?.removeAttribute("aria-labelledby");
            }

            const currentRoot = resolveRootElem();
            if (currentRoot)
            {
                observer.observe(currentRoot, { attributes: true, attributeFilter: ["aria-readonly", "id"] });
            }
        });

        onUnmounted(() =>
        {
            observer.disconnect();
        });

        const containerId = nextAutoId();
        const describedById = nextAutoId();
        const datepickerRef = ref<ComponentInstance<typeof DateTimePickerModal> & CalendarExposed & TimeSelectorExposed>();

        return () =>
        (
            <>
                <input
                    type="text"
                    class={DateTimePickerClasses.input}
                    placeholder="MM/DD/YYYY HH:MM XM"
                    readonly={readonly.value}
                    autocomplete={props.autocomplete ?? "off"}
                    ref={inputRef}
                    onClick={handleClick}
                    onFocusin={handleFocusIn}
                    onBeforeinput={handleBeforeInput}
                    onInput={handleInput}
                    onChange={handleChange}
                    onKeyup={handleKeyup}
                    onKeydown={handleKeydown}
                    onPaste={handlePaste}
                    value={formatDateTime(valueAsDate.value)}
                    id={hostId.value ? `${hostId.value}_input` : undefined}
                    data-dependents={JSON.stringify(props.dependents)}
                    role="combobox"
                    aria-expanded={modalOpen.value == false ? false : true}
                    aria-haspopup="dialog"
                    aria-controls={containerId}
                    aria-autocomplete="none"
                    aria-describedby={readonly.value ? readOnlyAnnouncementID : describedById}
                />
                {
                    (modalOpen.value === true) &&
                    <VueModalLayer
                        containerId={containerId}
                        expose={[getModalAnchorElement()]}
                        anchorTo={getModalAnchorElement()}
                        preserveFocus={(reason) => reason === "escape"}
                        ariaLabel="Choose date"
                        onClose={closeModal}
                    >
                        <DateTimePickerModal
                            ref={datepickerRef}
                            inputRef={inputRef}
                            close={closeModal}
                            value={valueAsDate.value}
                            onUpdate:value={(newValue) =>
                            {
                                setValueFromUserInput(newValue);
                            }}
                        />
                    </VueModalLayer>
                }
                <Svg
                    name="DateTime"
                    aria-haspopup="menu"
                    role="button"
                    showHoverEffect={false}
                    class={DateTimePickerClasses.svg}
                    onClick={() => toggleModal("svg click")}
                    isButton={!readonly.value}
                    aria-label="Date Time Picker"
                />
                <div id={describedById} class={DateTimePickerClasses.helpText}>Type a date and time or use the down arrow key to navigate to the date time picker</div>
            </>
        );

        function getModalAnchorElement()
        {
            const container = inputRef.value!.parentElement!;
            return props.contained
                ? container.parentElement!
                : container;
        }

        async function closeModal(_reason?: string)
        {
            modalOpen.value = "closing";
            await nextTick();
            modalOpen.value = false;

            if (document.activeElement !== inputRef.value)
            {
                suppressNextFocus.value = true;
                inputRef.value?.focus();
            }

            if (value.value)
            {
                selectPart(DateTimeParts.Month);
            }
        }

        function handlePaste(event: ClipboardEvent)
        {
            event.preventDefault();

            const pastedText = event.clipboardData?.getData('text/plain')
                .replace(new RegExp(`\\b(?:${DAYS_OF_WEEK.join("|")})\\b`, "g"), "");
            const date = new Date(pastedText ?? "");
            if (!pastedText || !date.isValidDate())
            {
                return false;
            }

            setValueFromUserInput(date);
            return false;
        }

        function handleFocusIn(event: FocusEvent)
        {
            const element = event.target as HTMLElement;
            const suppressFocus = element.getAttribute("data-suppress-focus") === "true" || resolveRootElem()?.getAttribute("data-suppress-focus") === "true";
            if (suppressFocus)
            {
                element.removeAttribute("data-suppress-focus");
                resolveRootElem()?.removeAttribute("data-suppress-focus");
                return;
            }
            if (suppressNextFocus.value)
            {
                suppressNextFocus.value = false;
                return;
            }
            handleOpenModal("focusin");
        }

        function selectPart(part: DateTimeParts)
        {
            const input = inputRef.value!;
            const currentValue = input.value;

            const firstSlash = currentValue.indexOf('/');
            const secondSlash = currentValue.indexOf('/', firstSlash + 1);
            const firstSpace = currentValue.indexOf(' ');
            const secondSpace = currentValue.indexOf(' ', firstSpace + 1);
            const colon = currentValue.indexOf(':');
            const lastSpace = currentValue.lastIndexOf(' ');
            const isDayOfWeekPresent = containsDay(currentValue);

            let startSelection: number | null = null;
            let endSelection: number | null = null;

            switch (part)
            {
                case DateTimeParts.Month:
                    startSelection = 0;
                    endSelection = firstSlash !== -1 ? firstSlash : currentValue.length;
                    break;

                case DateTimeParts.Day:
                    if (firstSlash !== -1)
                    {
                        startSelection = firstSlash + 1;
                        endSelection = secondSlash !== -1 ? secondSlash : currentValue.length;
                    }
                    break;

                case DateTimeParts.Year:
                    if (secondSlash !== -1)
                    {
                        startSelection = secondSlash + 1;
                        endSelection = firstSpace !== -1 ? firstSpace : currentValue.length;
                    }
                    break;

                case DateTimeParts.Hour:
                    if (colon !== -1)
                    {
                        if (secondSpace !== -1 && isDayOfWeekPresent)
                        {
                            startSelection = secondSpace + 1;
                        }
                        else
                        {
                            startSelection = firstSpace + 1;
                        }
                        endSelection = colon;
                    }
                    break;

                case DateTimeParts.Minute:
                    if (colon !== -1)
                    {
                        startSelection = colon + 1;
                        endSelection = Math.min(colon + 3, currentValue.length);
                    }
                    break;

                case DateTimeParts.AmPm:
                    if (lastSpace !== -1 && lastSpace > colon)
                    {
                        startSelection = lastSpace + 1;
                        endSelection = currentValue.length;
                    }
                    break;
            }

            if (startSelection !== null && endSelection !== null)
            {
                input.setSelectionRange(startSelection, endSelection);
            }
        }

        async function handleKeydown(event: KeyboardEvent)
        {
            const eventKeys = window.skyward.keys.getKeyNamesFromEvent(event);

            if (eventKeys.ARROW_RIGHT || eventKeys.ARROW_LEFT)
            {
                event.preventDefault();
                event.stopPropagation();
                const start = (event.target! as HTMLInputElement).selectionStart!;
                const end = (event.target! as HTMLInputElement).selectionEnd!;

                handleSelection(eventKeys.ARROW_LEFT, eventKeys.ARROW_RIGHT, start, end);
                return;
            }

            if (!modalOpen.value && eventKeys.ALT && (eventKeys.ARROW_DOWN || eventKeys.ARROW_UP))
            {
                handleOpenModal("keydown");
            }

            if (eventKeys.ENTER)
            {
                event.preventDefault();
                event.stopPropagation();

                if (modalOpen.value)
                {
                    modalOpen.value = false;
                }

                const date = parseDateTimeInput(inputRef.value!.value);
                setValueFromUserInput(date);
                return;
            }

            if (modalOpen.value && eventKeys.TAB)
            {
                // Closing the modal will cause a re-render and reset our changes. We have to update now instead of waiting for onBlur
                modalOpen.value = false;
                const date = parseDateTimeInput(inputRef.value!.value);
                setValueFromUserInput(date);
                return;
            }

            if (eventKeys.ARROW_UP || eventKeys.ARROW_DOWN)
            {
                event.preventDefault();
                event.stopPropagation();

                if (!modalOpen.value)
                {
                    handleOpenModal("keydown");
                    await nextTick();
                }

                datepickerRef.value?.focusCurrentDay();
            }
        }

        function getSelectedPart(start: number, end: number): DateTimeParts | undefined
        {
            const input = inputRef.value!;
            const currentValue = input.value;

            const firstSlash = currentValue.indexOf('/');
            const secondSlash = currentValue.indexOf('/', firstSlash + 1);
            const firstSpace = currentValue.indexOf(' ');
            const secondSpace = currentValue.indexOf(' ', firstSpace + 1);
            const isDayOfWeekPresent = containsDay(currentValue);
            const colon = currentValue.indexOf(':');
            const lastSpace = currentValue.lastIndexOf(' ');

            // Compute ranges dynamically
            const monthRange = firstSlash !== -1 ? [0, firstSlash] : null;
            const dayRange = secondSlash !== -1 ? [firstSlash + 1, secondSlash] : null;
            const yearRange = secondSlash !== -1 ? [secondSlash + 1, firstSpace !== -1 ? firstSpace : currentValue.length] : null;
            let hourRange = null;
            if (colon !== -1)
            {
                if (secondSpace !== -1 && isDayOfWeekPresent)
                {
                    hourRange = [secondSpace + 1, colon]
                }
                else
                {
                    hourRange = [firstSpace + 1, colon]
                }
            }
            const minuteRange = colon !== -1 ? [colon + 1, Math.min(colon + 3, currentValue.length)] : null;
            const amPmRange = lastSpace > colon ? [lastSpace + 1, currentValue.length] : null;

            const isInRange = (range: number[] | null) => range && start >= range[0] && end <= range[1];

            const partsOrder: { range: number[] | null; part: DateTimeParts }[] = [
                { range: monthRange, part: DateTimeParts.Month },
                { range: dayRange, part: DateTimeParts.Day },
                { range: yearRange, part: DateTimeParts.Year },
                { range: hourRange, part: DateTimeParts.Hour },
                { range: minuteRange, part: DateTimeParts.Minute },
                { range: amPmRange, part: DateTimeParts.AmPm }
            ].filter(p => p.range);

            // Check if selection is inside any part
            const match = partsOrder.find(p => isInRange(p.range));
            if (match) return match.part;

            // ✅ Special case: click in weekday gap → treat as Day
            if (yearRange && hourRange && start > yearRange[1] && start < hourRange[0])
            {
                return DateTimeParts.Day;
            }

            return undefined;
        }

        function handleSelection(left: boolean, right: boolean, start: number, end: number)
        {
            const partsOrder = [
                DateTimeParts.Month,
                DateTimeParts.Day,
                DateTimeParts.Year,
                DateTimeParts.Hour,
                DateTimeParts.Minute,
                DateTimeParts.AmPm
            ];

            const currentPart = getSelectedPart(start, end);
            const currentIndex = currentPart !== undefined ? partsOrder.indexOf(currentPart) : -1;

            if (right)
            {
                const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % partsOrder.length;
                selectPart(partsOrder[nextIndex]);
                return;
            }

            if (left)
            {
                const prevIndex = currentIndex === -1 ? partsOrder.length - 1 : (currentIndex - 1 + partsOrder.length) % partsOrder.length;
                selectPart(partsOrder[prevIndex]);
                return;
            }

            if (currentPart !== undefined)
            {
                selectPart(currentPart);
            }
        }

        async function handleKeyup(event: KeyboardEvent)
        {
            if (readonly.value)
            {
                return;
            }

            const eventKeys = window.skyward.keys.getKeyNamesFromEvent(event);
            const shortcutDate = now();

            // Handle shortcut keys (T = Today, R = Tomorrow, Y = Yesterday, C = Current, N = Now)
            if (eventKeys.T || eventKeys.R || eventKeys.Y || eventKeys.C || eventKeys.N)
            {
                if (eventKeys.R)
                {
                    shortcutDate.setDate(shortcutDate.getDate() + 1);
                    setTime();
                }
                else if (eventKeys.Y)
                {
                    shortcutDate.setDate(shortcutDate.getDate() - 1);
                    setTime();
                }
                else if (eventKeys.T)
                {
                    setTime();
                }

                setValueFromUserInput(shortcutDate);
                await nextTick();
                selectPart(DateTimeParts.Hour);
            }

            function setTime()
            {
                if (!valueAsDate.value)
                {
                    return;
                }

                shortcutDate.setHours(valueAsDate.value.getHours());
                shortcutDate.setMinutes(valueAsDate.value.getMinutes());
                shortcutDate.setSeconds(valueAsDate.value.getSeconds());
            }
        }

        function toggleModal(cause: string)
        {
            if (modalOpen.value === true)
            {
                closeModal();
                return;
            }

            handleOpenModal(cause);
        }

        function handleOpenModal(cause: string)
        {
            if (readonly.value || modalOpen.value !== false)
            {
                return;
            }
            modalOpen.value = true;
            rootElem?.scrollIntoView({ block: 'nearest' });

            if (cause === "svg click" && inputRef.value)
            {
                inputRef.value.focus();
            }
        }

        function handleClick(event: MouseEvent)
        {
            const start = (event.target! as HTMLInputElement).selectionStart!;
            const end = (event.target! as HTMLInputElement).selectionEnd!;

            handleSelection(false, false, start, end);
        }

        function handleChange(event: Event)
        {
            event.preventDefault();
            event.stopPropagation();
            const date = parseDateTimeInput(inputRef.value!.value.slice(0, 19));
            setValueFromUserInput(date);
        }

        function handleBeforeInput(event: Event)
        {
            const inputEvent = event as InputEvent;
            const inputData = inputEvent.data?.toUpperCase();
            const input = event.target as HTMLInputElement;

            // If data is being entered into the input, validate it
            if (inputData)
            {
                if (isNaN(parseInt(inputData)))
                {
                    // Allow the ":", "P", or "A" character if it's the first one
                    if (inputData === ":" && !input.value.includes(":") && input.value.length > 0)
                    {
                        return;
                    }
                    else if (inputData === "P" && !input.value.includes("P") && input.value.length > 0)
                    {
                        if (input.value.includes("A"))
                        {
                            input.value = input.value.replace("AM", "PM");
                            inputEvent.preventDefault();
                        }
                        return;
                    }
                    else if (inputData === "A" && !input.value.includes("A") && input.value.length > 0)
                    {
                        if (input.value.includes("P"))
                        {
                            input.value = input.value.replace("PM", "AM");
                            inputEvent.preventDefault();
                        }
                        return;
                    }

                    // Only allow "/" characters if there isn't already 2 of them present
                    if (inputData === "/" && !hasTwoSlashes(input.value))
                    {
                        return;
                    }

                    inputEvent.preventDefault(); // Stops the character from being inserted
                }
                return;
            }

            if (inputEvent.inputType === "deleteContentForward" && input.selectionEnd === input.selectionStart)
            {
                const selectionEnd = input.selectionEnd ?? 0;
                const value = input.value;
                // Check if the character after the selection is a space
                if (value[selectionEnd] === " ")
                {
                    // Move selection to the character after the space
                    input.setSelectionRange(selectionEnd + 1, selectionEnd + 1);
                }
            }

            if (inputEvent.inputType === "deleteContentBackward" && input.selectionEnd === input.selectionStart && !containsDay(input.value))
            {
                const selectionEnd = input.selectionEnd ?? 0;
                const value = input.value;
                // Check if the character before the selection is a space
                if (value[selectionEnd - 1] === " ")
                {
                    // Move selection to the character before the space
                    input.setSelectionRange(selectionEnd - 1, selectionEnd - 1);
                }
            }
        }

        async function handleInput(event: Event)
        {
            const input = event.target as HTMLInputElement;
            let selectionEnd = input.selectionEnd!;
            let shortenSelection = false;
            if (containsDay(input.value) && selectionEnd >= DAY_OF_WEEK_END_POSITION)
            {
                shortenSelection = true;
            }
            const newValue = input.value.replace(/[^\dap:/ ]/gi, ""); // Keep digits, a, p, colon, space, slash

            // Collapses multiple spaces and keeps time intact
            const [datePart, ...rest] = newValue.trim().split(/\s+/);
            let timePartRaw = rest.join(' ');

            // Split into date and time parts
            const dateParts = datePart ? datePart.split('/') : [];
            const timeParts = timePartRaw ? timePartRaw.replace(/[^0-9:]/g, "").split(':') : [];

            // Handle date overflow
            let remainingMonth = "";
            let remainingDay = "";
            let remainingYear = "";
            if (dateParts.length > 0)
            {
                remainingMonth = dateParts[0].slice(2);
                dateParts[0] = dateParts[0].slice(0, 2);

                // Don't allow an invalid month to be entered
                const monthValue = parseInt(dateParts[0]);
                if (monthValue > 12)
                {
                    remainingMonth = dateParts[0].slice(1);
                    dateParts[0] = "0" + dateParts[0].slice(0, 1);
                }

                if (remainingMonth.length > 0 && dateParts.length < 2)
                {
                    dateParts.push(remainingMonth);
                }
            }
            if (dateParts.length > 1)
            {
                if (remainingMonth.length > 0)
                {
                    dateParts[1] = remainingMonth + dateParts[1].slice(1, 2);
                    selectionEnd = 4;
                }
                else
                {
                    if (selectionEnd !== 5)
                    {
                        remainingDay = dateParts[1].slice(2);
                    }

                    dateParts[1] = dateParts[1].slice(0, 2);

                    // Don't allow an invalid day be entered
                    const daysInMonth = new Date(2000, parseInt(dateParts[0]), 0).getDate(); // Leap Year to allow for 29 (will be cast after change event if not a leap year)
                    const dayValue = parseInt(dateParts[1]);
                    if (dayValue > daysInMonth)
                    {
                        remainingDay = dateParts[1].slice(1);
                        dateParts[1] = "0" + dateParts[1].slice(0, 1);
                    }

                    if (remainingDay.length > 0 && dateParts.length < 3)
                    {
                        dateParts.push(remainingDay);
                    }
                }
            }
            if (dateParts.length > 2)
            {
                let yearLowerCase = dateParts[2].toLowerCase();

                if (yearLowerCase.includes("a"))
                {
                    dateParts[2] = yearLowerCase.replace("a", "");
                    timePartRaw += "a";
                }
                else if (yearLowerCase.includes("p"))
                {
                    dateParts[2] = yearLowerCase.replace("p", "");
                    timePartRaw += "p";
                }

                if (remainingDay.length > 0)
                {
                    dateParts[2] = remainingDay + dateParts[2].slice(1, 4);
                    selectionEnd = 7;
                }
                else
                {
                    remainingYear = dateParts[2].slice(4);
                    dateParts[2] = dateParts[2].slice(0, 4);
                }

                if (remainingYear.length > 0 && timeParts.length < 1)
                {
                    timeParts.push(remainingYear);
                }
            }

            // Handle time overflow
            let remainingHour = "";

            let hour = "";
            if (timeParts.length > 0)
            {
                if (remainingYear.length > 0)
                {
                    hour = remainingYear + timeParts[0].slice(1, 2)
                    selectionEnd = HOUR_START_POSITION;
                }
                else
                {
                    remainingHour = timeParts[0].slice(2);
                    hour = timeParts[0].slice(0, 2);
                }

                if (remainingHour.length > 0 && timeParts.length < 2)
                {
                    timeParts.push(remainingHour);
                }
            }

            let minute = "";
            if (timeParts.length > 1)
            {
                if (remainingHour.length > 0)
                {
                    minute = remainingHour + timeParts[1].slice(1, 2)
                    selectionEnd = MINUTE_START_POSITION;
                }
                else
                {
                    minute = timeParts[1].slice(0, 2);
                }
            }

            let amPm = "";
            if (timePartRaw)
            {
                const amPmMatch = timePartRaw.match(/(am|pm|a|p)$/i);
                if (amPmMatch)
                {
                    amPm = amPmMatch[0].toUpperCase();

                    if (amPm.length === 1 && (event as InputEvent).inputType.startsWith("delete"))
                    {
                        amPm = "";
                    }
                    else
                    {
                        let checkTime = false;
                        if (amPm === "A")
                        {
                            amPm = "AM";
                            checkTime = true;
                        }
                        else if (amPm === "P")
                        {
                            amPm = "PM";
                            checkTime = true;
                        }

                        if (checkTime)
                        {
                            if (minute.length === 0)
                            {
                                minute = "00";
                            }
                            if (hour.length === 0)
                            {
                                hour = "12";
                            }
                        }

                        if (selectionEnd == input.value.length)
                        {
                            selectionEnd = END_POSITION;
                            shortenSelection = false;
                        }
                    }
                }
            }

            // Rebuild value
            let formattedValue = dateParts.join('/');
            if (hour || minute || amPm)
            {
                formattedValue += ' ';
                if (hour) formattedValue += hour;
                if (minute) formattedValue += ':' + minute;
                if (amPm) formattedValue += ' ' + amPm;
            }

            if (shortenSelection)
            {
                selectionEnd -= 4;
            }

            input.value = formattedValue;
            await nextTick();
            input.setSelectionRange(selectionEnd, selectionEnd);
        }


        function setValueFromUserInput(newDate: Date | undefined)
        {
            const normalizedDate = newDate ? new Date(newDate) : undefined;
            normalizedDate?.setSeconds(0);
            inputRef.value!.value = formatDateTime(normalizedDate) ?? "";

            if (valueAsDate.value?.valueOf() !== normalizedDate?.valueOf())
            {
                valueAsDate.value = normalizedDate;
                const event = new CustomEvent('change', { bubbles: true, detail: { internal: true } });
                resolveRootElem()?.dispatchEvent(event);
            }
        }
    }
});

class SkyDateTimePicker extends DateTimePicker
{
    connectedCallback()
    {
        // Clear any pre-existing inner HTML (e.g., serialized by CKEditor) before Vue mounts,
        // so that Vue's own render does not produce duplicate elements.
        if (!(this as any)._instance)
        {
            this.innerHTML = '';
        }

        super.connectedCallback();
    }

    get valueAsDate() { return (this as any)._valueAsDateRef().value; }
    set valueAsDate(newValue) { (this as any)._valueAsDateRef().value = newValue; }
    /**
     * Close the datepicker modal programatically.
     * This calls the function exposed from the component setup via `context.expose`.
     */
    closeModal(): Promise<void> | void
    {
        const fn = (this as any)._closeModal;
        if (typeof fn === "function")
        {
            return fn();
        }
    }
}
export default SkyDateTimePicker;

export function parseDateTimeInput(value: string): Date | undefined
{
    if (!value || value.trim() === '') return undefined;

    const currentDate = now();

    // Split into date and time parts
    const [datePart, timePartRaw, amPmPartRaw] = value.trim().split(/\s+/);
    const dateParts = datePart ? datePart.split('/') : [];

    let month: number | undefined;
    let day: number | undefined;
    let year: number = currentDate.getFullYear();
    let hour: number = 0;
    let minute: number = 0;

    // ✅ Parse Date
    if (dateParts.length > 0)
    {
        month = parseInt(dateParts[0]);
        if (isNaN(month)) return undefined;
        month = Math.min(Math.max(month, 1), 12);
    }
    else
    {
        return undefined;
    }

    if (dateParts.length > 1)
    {
        day = parseInt(dateParts[1]);
        if (isNaN(day) || day < 1)
        {
            day = 1;
        }
        else
        {
            const daysInMonth = new Date(year, month, 0).getDate();
            day = Math.min(day, daysInMonth);
        }
    }
    else
    {
        day = 1;
    }

    if (dateParts.length > 2)
    {
        // Do some year inference
        const setYear = dateParts[2];
        if (setYear.length < 3 && setYear.length > 0)
        {
            const yearValue = parseInt(setYear);
            if (yearValue > 55)
            {
                dateParts[2] = `19${setYear}`;
            }
            else
            {
                const partToPrepend = setYear.length === 2 ? "20" : "200"
                dateParts[2] = partToPrepend + setYear
            }
        }

        year = parseInt(dateParts[2]);

        // Fallback
        if (isNaN(year))
        {
            year = currentDate.getFullYear();
        }
    }

    // ✅ Parse Time
    if (timePartRaw)
    {
        const timeParts = timePartRaw.split(':');
        if (timeParts.length > 0)
        {
            hour = parseInt(timeParts[0]);
            if (isNaN(hour)) hour = 0;
        }
        if (timeParts.length > 1)
        {
            minute = parseInt(timeParts[1]);
            if (isNaN(minute)) minute = 0;
        }
    }

    // ✅ Handle AM/PM
    const amPm = amPmPartRaw ? amPmPartRaw.toUpperCase() : '';
    if (amPm === 'PM' && hour < 12)
    {
        hour += 12;
    }
    else if (amPm === 'AM' && hour === 12)
    {
        hour = 0;
    }

    const date = new Date(year, month - 1, day, hour, minute); // month is zero-based in Date constructor
    date.setUTCFullYear(year); // The JS Date constructor doesn't always honor your year value and may cast it.
    return date;
}


export function formatDateTime(date?: Date)
{
    if (!date)
    {
        return undefined;
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const dayOfWeek = DAYS_OF_WEEK[date.getDay()];
    let hour = date.getHours();
    let amPm: "AM" | "PM" = "AM";
    if (hour >= 12)
    {
        amPm = "PM";

        if (hour !== 12)
        {
            hour = hour % 12;
        }
    }
    else if (hour == 0)
    {
        hour = 12;
    }
    const hourDisplay = String(hour).padStart(2, '0');
    const timeDisplay = String(date.getMinutes()).padStart(2, '0');

    return `${month}/${day}/${String(year).padStart(4, "0")} ${dayOfWeek} ${hourDisplay}:${timeDisplay} ${amPm}`;
}

function containsDay(input: string): boolean
{
    return DAYS_OF_WEEK.some(day => input.includes(day));
}

function hasTwoSlashes(input: string): boolean
{
    return (input.split("/").length - 1) >= 2;
}
